import React, { useCallback, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Form } from 'react-bootstrap'
import classNames from 'classnames'

import { getIntegrationDisplayName } from '../../../common/integrations'

import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import { Button } from '../../../components/Button/Button'

import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  selectDocumentProspectingIntegrationHasDynamicSchema,
  selectDocumentProspectingServiceId,
  resetDocumentProspecting,
  selectDocumentProspectingIntegrationContact,
  selectDocumentProspectingBaseFormValues,
  selectDocumentProspectingContact,
} from '../../../store/processDocument/documentProspecting'

import { DocumentProspectingStep, DocumentProspectingStepProps, ProspectingBaseFormValues } from '../../../models/DocumentProspecting'
import { User } from '../../../models/User'

import { useDocumentProspectingContacts, useProspectingBaseForm, useShowSummaryForDocumentProspecting } from './hooks'

const _CURRENT_STEP = DocumentProspectingStep.BASE_FORM

interface ProspectingBaseFormProps extends DocumentProspectingStepProps {
  initialBaseFormValues: ProspectingBaseFormValues
}

export const ProspectingBaseForm: React.FC<ProspectingBaseFormProps> = ({ activeStep, onActivate, onComplete, initialBaseFormValues }) => {
  const { tracked } = useTracking()

  const integrationHasDynamicSchema = useAppSelector(selectDocumentProspectingIntegrationHasDynamicSchema)
  const integrationContact = useAppSelector(selectDocumentProspectingIntegrationContact)

  const { control, handleSubmit, formState } = useForm<ProspectingBaseFormValues>({ defaultValues: initialBaseFormValues, mode: 'onChange' })

  const { submitFormValues, submitLoading } = useProspectingBaseForm()

  const isActive = activeStep === _CURRENT_STEP
  const isComplete = activeStep > _CURRENT_STEP

  const _onSubmit = useCallback(
    async (baseFormValues: ProspectingBaseFormValues) => {
      const isSuccess = await submitFormValues(baseFormValues)
      if (!isSuccess) {
        return
      }
      onComplete()
    },
    [onComplete, submitFormValues],
  )

  const onSubmit = tracked(_onSubmit, { eventName: TrackEvent.DOCUMENT_PROSPECTING_CONTACT_CONFIRMED })

  const handleActive = useCallback(() => {
    if (integrationHasDynamicSchema) {
      return
    }
    onActivate(_CURRENT_STEP)
  }, [integrationHasDynamicSchema, onActivate])

  if (!isActive && !isComplete) {
    return (
      <div className='d-flex align-items-center gap-3'>
        <div className='z-1 border border-4 border-start-0 border-end-0 border-white'>
          <div className='document-prospecting-step-icon-wrapper rounded-2 d-flex align-items-center justify-content-center zocks-border-color-gray-200 zocks-bg-color-gray-100 border'>
            <i className='bi bi-person-lines-fill zocks-text-gray-500' />
          </div>
        </div>
        <div className='d-flex align-items-center document-prospecting-step-header-wrapper'>
          <h5 className='zocks-text-gray-500'>Complete contact information</h5>
        </div>
      </div>
    )
  }

  return (
    <div className='d-flex flex-column gap-3'>
      <div className='d-flex gap-3 align-items-start'>
        <div className='z-1 border border-4 border-start-0 border-end-0 border-white'>
          <div
            className={classNames('document-prospecting-step-icon-wrapper rounded-2 d-flex align-items-center justify-content-center', {
              'zocks-border-color-gray-200 zocks-bg-color-gray-100 border': !isComplete,
              'zocks-bg-color-success-background': isComplete || (!submitLoading && integrationContact),
            })}
          >
            {isComplete || (!submitLoading && integrationContact) ? (
              <i className='bi bi-check zocks-text-success' />
            ) : (
              <i className='bi bi-person-lines-fill' />
            )}
          </div>
        </div>
        <div className='d-flex flex-column'>
          <div className='d-flex align-items-center document-prospecting-step-header-wrapper'>
            <h5>Complete contact information</h5>
          </div>
          <div className='d-flex align-items-center gap-1'>
            <span className='paragraph-small'>Add missing information to continue.</span>
            {isComplete && !integrationContact ? (
              <div
                className={classNames('button-small p-0 border-0', {
                  'zocks-text-gray-500': integrationHasDynamicSchema,
                  'zocks-text-primary cursor-pointer': !integrationHasDynamicSchema,
                })}
                onClick={handleActive}
              >
                Change
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {!isComplete ? (
        !submitLoading && integrationContact ? (
          <ContactExistsBadge integrationContact={integrationContact} />
        ) : (
          <div className='d-flex flex-column gap-4 zocks-pl-3.0'>
            <div className='d-flex align-items-center gap-3'>
              <div className='d-flex flex-column gap-1'>
                <span className='paragraph-medium'>First name</span>
                <Controller
                  control={control}
                  name='firstName'
                  rules={{ required: 'First name is required.', minLength: 1 }}
                  render={({ field }) => <Form.Control type='text' value={field.value} onChange={field.onChange} disabled={submitLoading} />}
                />
              </div>
              <div className='d-flex flex-column gap-1'>
                <span className='paragraph-medium'>Last name</span>
                <Controller
                  control={control}
                  name='lastName'
                  rules={{ required: 'Last name is required.', minLength: 1 }}
                  render={({ field }) => <Form.Control type='text' value={field.value} onChange={field.onChange} disabled={submitLoading} />}
                />
              </div>
            </div>
            <div className='d-flex flex-column gap-1'>
              <span className='paragraph-medium'>Email address</span>
              <Controller
                control={control}
                name='email'
                rules={{ required: 'Email address is required.', minLength: 1 }}
                render={({ field }) => <Form.Control type='text' value={field.value} onChange={field.onChange} disabled={submitLoading} />}
              />
            </div>
            <div>
              <Button variant='primary' onClick={handleSubmit(onSubmit)} loading={submitLoading} disabled={!formState.isValid || submitLoading}>
                {integrationHasDynamicSchema ? 'Create contact' : 'Continue'}
              </Button>
            </div>
          </div>
        )
      ) : null}
    </div>
  )
}

interface ContactExistsBadgeProps {
  integrationContact: User
}

const ContactExistsBadge: React.FC<ContactExistsBadgeProps> = ({ integrationContact }) => {
  const dispatch = useAppDispatch()

  const serviceId = useAppSelector(selectDocumentProspectingServiceId)
  const baseFormValues = useAppSelector(selectDocumentProspectingBaseFormValues)
  const contact = useAppSelector(selectDocumentProspectingContact)

  const [loading, setLoading] = useState<boolean>(false)

  const { showSummary } = useShowSummaryForDocumentProspecting()
  const { createContact } = useDocumentProspectingContacts()

  const handleShowSummary = useCallback(async () => {
    if (!baseFormValues) {
      return
    }
    setLoading(true)
    try {
      if (!contact) {
        await createContact(baseFormValues)
      }
      await showSummary()
    } finally {
      setLoading(false)
    }
  }, [baseFormValues, contact, createContact, showSummary])

  const handleChooseDifferentFile = () => {
    dispatch(resetDocumentProspecting())
  }

  if (!serviceId || !integrationContact) {
    return null
  }

  const primaryText = contact
    ? `A contact with ${contact.email} already exists in Zocks and ${getIntegrationDisplayName(serviceId)}.`
    : `A contact with ${integrationContact.email} already exists in ${getIntegrationDisplayName(serviceId)}.`

  const secondaryText = contact
    ? `Summarize the document to update the existing contact, or upload a different file.`
    : `Summarize the document to create the contact in Zocks and link to the existing ${getIntegrationDisplayName(serviceId)} contact, or upload a different file.`

  return (
    <div className='d-flex flex-column gap-3 zocks-pl-3.0'>
      <div className='d-flex flex-column'>
        <span className='paragraph-medium bold'>{primaryText}</span>
        <span className='paragraph-medium'>{secondaryText}</span>
      </div>
      <div className='d-flex align-items-center gap-3'>
        <Button variant='primary' loading={loading} onClick={handleShowSummary}>
          Summarize document
        </Button>
        <Button variant='grayGhost' onClick={handleChooseDifferentFile}>
          Choose different file
        </Button>
      </div>
    </div>
  )
}
